export interface AnonymousMigration {
    etag: string;
    projectId: string;
}

export interface AnonymousProjectDraft {
    content: Blob;
    id: string;
    migration?: AnonymousMigration;
    name: string;
    thumbnail?: Blob;
    updatedAt: number;
}

const DATABASE_NAME = "scratch-cg-anonymous";
const DATABASE_VERSION = 1;
const DRAFT_KEY = "current";
const STORE_NAME = "drafts";

const requestResult = <T>(request: IDBRequest<T>): Promise<T> =>
    new Promise((resolve, reject) => {
        request.addEventListener("success", () => resolve(request.result));
        request.addEventListener("error", () =>
            reject(request.error ?? new Error("INDEXED_DB_REQUEST_FAILED")),
        );
    });

const transactionComplete = (transaction: IDBTransaction): Promise<void> =>
    new Promise((resolve, reject) => {
        transaction.addEventListener("complete", () => resolve());
        transaction.addEventListener("abort", () =>
            reject(transaction.error ?? new Error("INDEXED_DB_ABORTED")),
        );
        transaction.addEventListener("error", () =>
            reject(transaction.error ?? new Error("INDEXED_DB_FAILED")),
        );
    });

const openDatabase = (factory: IDBFactory): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        const request = factory.open(DATABASE_NAME, DATABASE_VERSION);
        request.addEventListener("upgradeneeded", () => {
            if (!request.result.objectStoreNames.contains(STORE_NAME))
                request.result.createObjectStore(STORE_NAME);
        });
        request.addEventListener("success", () => resolve(request.result));
        request.addEventListener("error", () =>
            reject(request.error ?? new Error("INDEXED_DB_OPEN_FAILED")),
        );
        request.addEventListener("blocked", () =>
            reject(new Error("INDEXED_DB_BLOCKED")),
        );
    });

export const readAnonymousProject = async (
    factory: IDBFactory = indexedDB,
): Promise<AnonymousProjectDraft | undefined> => {
    const database = await openDatabase(factory);
    try {
        const transaction = database.transaction(STORE_NAME, "readonly");
        const complete = transactionComplete(transaction);
        const result = await requestResult<AnonymousProjectDraft | undefined>(
            transaction.objectStore(STORE_NAME).get(DRAFT_KEY) as IDBRequest<
                AnonymousProjectDraft | undefined
            >,
        );
        await complete;
        return result;
    } finally {
        database.close();
    }
};

export const writeAnonymousProject = async (
    draft: AnonymousProjectDraft,
    factory: IDBFactory = indexedDB,
): Promise<void> => {
    const database = await openDatabase(factory);
    try {
        const transaction = database.transaction(STORE_NAME, "readwrite");
        transaction.objectStore(STORE_NAME).put(draft, DRAFT_KEY);
        await transactionComplete(transaction);
    } finally {
        database.close();
    }
};

export const clearAnonymousProject = async (
    factory: IDBFactory = indexedDB,
): Promise<void> => {
    const database = await openDatabase(factory);
    try {
        const transaction = database.transaction(STORE_NAME, "readwrite");
        transaction.objectStore(STORE_NAME).delete(DRAFT_KEY);
        await transactionComplete(transaction);
    } finally {
        database.close();
    }
};
